import { useCallback, useState } from 'react';
import { QueryModel, SearchModel, SortModel, UseDataTableSearchParams } from './DataTableQuery.types';
import { useDataTableContext } from '../DataTableContext';
import { useSearchStringStorage } from './params/useSearchStringStorage';
import { searchModelMapper } from './params/searchModelMapper';

export const useDataTableSearchParams = <Q extends QueryModel, S extends SortModel>(): UseDataTableSearchParams<
  Q,
  S
> => {
  const { querySettings } = useDataTableContext();
  const { getSearchString, setSearchString } = useSearchStringStorage({
    useUrlParams: querySettings?.useParams,
    useStorageKey: querySettings?.previousURLKey
  });
  const [initialParams] = useState<SearchModel<Q, S>>(searchModelMapper.fromSearchString(getSearchString()));

  const saveParamsInStorage = useCallback(
    (nextParams: SearchModel<Q, S>) => {
      setSearchString(searchModelMapper.toSearchString(nextParams));
    },
    [setSearchString]
  );

  const saveParams = useCallback(
    (newParams: SearchModel<Q, S>) => {
      const nextParams = searchModelMapper.cleanParams({
        ...getCustomQueryParameters(querySettings?.excludeCustomParameters),
        ...newParams
      });
      saveParamsInStorage(nextParams);
    },

    [querySettings?.excludeCustomParameters, saveParamsInStorage]
  );

  return {
    initialParams,
    saveParams
  };
};

const getCustomQueryParameters = (excludeCustomParameters: string[] = []): Object => {
  const queryParams = new URLSearchParams(window.location.search);
  return excludeCustomParameters.reduce((accum, excludeCustomParameter) => {
    const excludeCustomParameterValue = queryParams.get(encodeURI(excludeCustomParameter));
    return {
      ...accum,
      ...(excludeCustomParameterValue ? { [excludeCustomParameter]: excludeCustomParameterValue } : {})
    };
  }, {});
};
